// Wave 13 / Track B — Per-region visibility from MediaPipe landmark
// confidence.
//
// The Hoerner-cylinder + position-LUT shielding model in sectional.py
// is correct in the average but doesn't respond to an individual rider's
// pose. A rider with arms tucked behind shoulders attributes the same
// arm watts as a rider with arms in front because the LUT only keys on
// position class (drops / tt / etc).
//
// MediaPipe Pose already emits a per-landmark `visibility` score that
// drops when a joint is occluded by another body part. Averaging those
// scores across each region gives us a pose-aware shielding weight
// that's strictly tighter than the LUT and requires no WebGL pass.
//
// The plan originally called for an offscreen z-buffer pass over the
// SMPL mesh. That's the next step if we need sub-region (per-vertex)
// shielding; for whole-region drag attribution the landmark approach is
// sufficient and ~50 lines vs ~300 of WebGL.

export interface RegionVisibility {
  head: number; // 0..1, mean visibility of head landmarks
  torso: number;
  arms: number;
  legs: number;
}

const REGION_LANDMARKS: Record<keyof RegionVisibility, number[]> = {
  // Nose + ears + eyes — drop visibility when the head turns far enough
  // that we can't see the contralateral ear, etc.
  head: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  // Shoulders + hips. Torso is rarely occluded by other body parts in
  // cycling poses, but visibility still moves with camera angle.
  torso: [11, 12, 23, 24],
  // Wave 20.35 — elbows + wrists ONLY. The old set included the index-
  // finger anchors [19, 20] which are almost always hidden behind aero
  // bars in a real cycling position; MediaPipe's confidence for those
  // stayed near zero the whole ride even when the arms were perfectly
  // recognizable, dragging the mean way down.
  arms: [13, 14, 15, 16],
  // Knees + ankles + feet. Legs occlude each other in aero positions
  // (the far leg is often hidden behind the near one).
  legs: [25, 26, 27, 28, 29, 30, 31, 32],
};

// Wave 20.35 — per-region EMA state. MediaPipe visibility per single
// frame is jumpy (a hand briefly hides behind a bar and confidence
// nose-dives for one frame). Smooth with an ~0.75 s EMA so downstream
// consumers (region drag attribution, coach triggers) see a stable
// signal that reflects "is this region actually visible on the ride?"
// rather than a single-frame occlusion.
const EMA_ALPHA = 0.15; // ~7 frames @ 30 fps ≈ 230 ms rise; smoothing feels responsive but stable
const _ema: RegionVisibility = { head: 0, torso: 0, arms: 0, legs: 0 };
let _emaSeeded = false;

/** Compute per-region visibility (mean of MediaPipe landmark visibility
 *  across the region's joints), smoothed with a short EMA. Returns null
 *  when landmarks are missing. */
export function computeRegionVisibility(
  landmarks: Array<{ visibility?: number }> | null,
): RegionVisibility | null {
  if (!landmarks || landmarks.length < 33) return null;
  const meanFor = (idxs: number[]): number => {
    let sum = 0;
    let count = 0;
    for (const i of idxs) {
      const lm = landmarks[i];
      if (!lm) continue;
      const v = typeof lm.visibility === 'number' ? lm.visibility : 0;
      sum += v;
      count++;
    }
    return count > 0 ? sum / count : 0;
  };
  const raw: RegionVisibility = {
    head:  meanFor(REGION_LANDMARKS.head),
    torso: meanFor(REGION_LANDMARKS.torso),
    arms:  meanFor(REGION_LANDMARKS.arms),
    legs:  meanFor(REGION_LANDMARKS.legs),
  };
  if (!_emaSeeded) {
    _ema.head = raw.head;
    _ema.torso = raw.torso;
    _ema.arms = raw.arms;
    _ema.legs = raw.legs;
    _emaSeeded = true;
  } else {
    _ema.head  = _ema.head  + EMA_ALPHA * (raw.head  - _ema.head);
    _ema.torso = _ema.torso + EMA_ALPHA * (raw.torso - _ema.torso);
    _ema.arms  = _ema.arms  + EMA_ALPHA * (raw.arms  - _ema.arms);
    _ema.legs  = _ema.legs  + EMA_ALPHA * (raw.legs  - _ema.legs);
  }
  return {
    head:  _ema.head,
    torso: _ema.torso,
    arms:  _ema.arms,
    legs:  _ema.legs,
  };
}

/** Reset the visibility EMA (call between sessions so the previous
 *  ride's tail doesn't leak into the next one). */
export function resetRegionVisibility(): void {
  _emaSeeded = false;
  _ema.head = 0;
  _ema.torso = 0;
  _ema.arms = 0;
  _ema.legs = 0;
}
